import { type FC, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useMediaQuery } from "react-responsive";

import classnames from "classnames";
import dompurify from "isomorphic-dompurify";

import { useConfig } from "@jti/app-context/useConfig";
import { useCheckoutConfiguration } from "@jti/commerce/providers";
import { AlertBanner } from "@jti/core-components/components/AlertBanner";
import type { Config } from "@jti/fe-common";
import { BREAKPOINT_MEDIUM, customPaymentMethods } from "@jti/fe-common";
import type { Order } from "@jti/magento";
import { useDispatch } from "@jti/store/reduxHooks";
import { setIsPaymentProcessing } from "@jti/store/slices/LEGACY__checkoutSlice";
import { DEPRECATED_Loading } from "@jti/ui";

import { DeliveryDetailsSection } from "@checkout/components/DeliveryDetailsSection";
import { ShippingDetailsSection } from "@checkout/components/ShippingDetailsSection";
import { SubscriptionBillingDisclaimer } from "@checkout/components/SubscriptionBillingDisclaimer";
import { useCheckoutSubscriptionDisplayState, useFailedPaymentFlow } from "@checkout/hooks";
import { useOrder } from "@checkout/hooks/useOrder";

import { PaymentPageFooter } from "../PaymentPageFooter";
import { PaymentPagePaymentMethodsContainer } from "../PaymentPagePaymentMethodsContainer";

import styles from "./PaymentPageContent.module.scss";

type PaymentPageContentProps = {
  asideSubmitButtonWrapperRef: React.RefObject<HTMLDivElement | null>;
};

const { sanitize } = dompurify;

const formatAddressRegion = (address: Order["shippingAddress"], config: Config) => {
  if (!address) {
    return undefined;
  }

  const regionName = typeof address.region === "string" ? address.region : (address.region as any)?.region;

  return {
    ...address,
    ...(!address.countryName &&
      config.COUNTRY_NAME && {
        countryName: config.COUNTRY_NAME,
      }),
    region: regionName
      ? {
          region_id: 0,
          region_code: "0",
          region: regionName,
        }
      : undefined,
  };
};

export const PaymentPageContent: FC<PaymentPageContentProps> = ({ asideSubmitButtonWrapperRef }) => {
  const dispatch = useDispatch();
  const { order, orderFetchStatus } = useOrder();
  const config = useConfig();

  const [paymentMethodCode, setPaymentMethodCode] = useState("");

  const { isFailedPaymentFlow } = useFailedPaymentFlow();
  const checkoutConfiguration = useCheckoutConfiguration();
  const { failedPaymentFlow } = checkoutConfiguration;

  const formattedAddress = useMemo(() => {
    if (!order?.shippingAddress) {
      return undefined;
    }
    return formatAddressRegion(order.shippingAddress, config);
  }, [order]);

  const isScreenAboveMobile = useMediaQuery({ minWidth: BREAKPOINT_MEDIUM });
  const { showConsentAndPaySubscriptionBillingDisclaimer } = useCheckoutSubscriptionDisplayState(order?.items ?? []);

  const isPayOnDeliverySelected = paymentMethodCode === customPaymentMethods.payOnDelivery;

  const handlePaymentMethodChange = (paymentMethod: string): void => {
    dispatch(setIsPaymentProcessing(false));
    setPaymentMethodCode(paymentMethod);
  };

  useEffect(() => {
    dispatch(setIsPaymentProcessing(false));

    //DESP-48488 don't use bfcache for checkout when user backs from payment gateway
    window.addEventListener("pageshow", (event) => {
      if (event.persisted) {
        window.location.reload();
      }
    });

    return () => {
      dispatch(setIsPaymentProcessing(false));
    };
  }, [dispatch]);

  return (
    <div
      data-js-id="payment-step-section"
      className={classnames(styles.additionInfo, {
        [styles.noSecureBlock]: isPayOnDeliverySelected,
      })}
    >
      {/* FAILED PAYMENT ERROR MESSAGE */}
      {isFailedPaymentFlow && (failedPaymentFlow?.title || failedPaymentFlow?.message) && (
        <div className={styles.messageBox}>
          <AlertBanner
            tone="error"
            title={failedPaymentFlow?.title}
            richDescription={<div dangerouslySetInnerHTML={{ __html: sanitize(failedPaymentFlow?.message) }} />}
          />
        </div>
      )}

      {/* SHIPPING DETAILS SECTION */}
      {orderFetchStatus === "pending" && <DEPRECATED_Loading />}
      {orderFetchStatus === "success" && order?.shippingAddress && (
        <ShippingDetailsSection
          shippingAddress={formattedAddress}
          companyName={order.shippingAddress.pickupInformation?.name}
          className={styles.shippingDetailsSection}
        />
      )}

      {/* SELECTED DELIVERY OPTION SECTION */}
      <DeliveryDetailsSection />

      {/* PAYMENT METHODS SELECTION */}
      <PaymentPagePaymentMethodsContainer
        paymentMethodCode={paymentMethodCode}
        handlePaymentMethodChange={handlePaymentMethodChange}
      />

      {showConsentAndPaySubscriptionBillingDisclaimer && <SubscriptionBillingDisclaimer />}

      {isScreenAboveMobile ? (
        <PaymentPageFooter paymentMethodCode={paymentMethodCode} data-testid="payment-page-footer-desktop" />
      ) : (
        asideSubmitButtonWrapperRef.current &&
        createPortal(
          <PaymentPageFooter paymentMethodCode={paymentMethodCode} data-testid="payment-page-footer-mobile" />,
          asideSubmitButtonWrapperRef.current
        )
      )}
    </div>
  );
};
